import React from "react";

import { AlertTriangle, ChevronRight, Inbox, Clock, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { WidgetCard } from "./StatWidgets";

// Jobs needing attention — the actionable worklist. Two columns: open jobs with
// zero applications, and open jobs stalled 14+ days. Each row clicks through to
// the job so the admin can act immediately. Age badges escalate by idle time so
// the most neglected reqs jump out.

const ageBadgeClasses = (days) => {
    if (days == null) return "bg-background-tertiary text-text-secondary border-border-primary";
    if (days >= 30) return "bg-red-500/10 text-red-500 border-red-500/20";
    if (days >= 14) return "bg-amber-500/10 text-amber-500 border-amber-500/20";
    return "bg-background-tertiary text-text-secondary border-border-primary";
};

const JobRow = ({ job, onClick }) => (
    <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.(e)}
        className="group w-full flex items-center gap-2 text-left rounded-lg px-2 py-2 hover:bg-background-tertiary/60 transition-colors cursor-pointer"
    >
        <div className="min-w-0 flex-1">
            <p className="text-[12px] text-text-primary leading-snug truncate">{job.title}</p>
            <p className="text-[10px] text-text-secondary truncate">
                {job.reason === "no_applications"
                    ? "No applications received"
                    : `${job.applicationCount} candidate${job.applicationCount === 1 ? "" : "s"} · no recent movement`}
                {job.priority ? ` · ${job.priority.toLowerCase()} priority` : ""}
            </p>
        </div>
        {job.daysSinceActivity != null ? (
            <span
                className={
                    "text-[9px] font-semibold rounded-full border px-1.5 py-[1px] flex-shrink-0 " +
                    ageBadgeClasses(job.daysSinceActivity)
                }
            >
                {job.daysSinceActivity}d idle
            </span>
        ) : null}
        <ChevronRight
            size={14}
            className="text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        />
    </div>
);

const Column = ({ title, icon: Icon, tint, jobs, loading, onJob, emptyText }) => (
    <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center justify-between px-1 pb-1 border-b border-border-primary">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-text-primary">
                <Icon size={13} style={{ color: tint }} />
                {title}
            </span>
            {!loading ? (
                <span
                    className="text-[10px] font-semibold rounded-full px-1.5 py-[1px]"
                    style={{ background: `${tint}1a`, color: tint }}
                >
                    {jobs.length}
                </span>
            ) : null}
        </div>
        {loading ? (
            [...Array(3)].map((_, i) => (
                <div key={i} className="h-9 w-full rounded-lg bg-background-tertiary animate-pulse" />
            ))
        ) : jobs.length === 0 ? (
            <p className="text-[11px] text-text-secondary py-6 text-center flex items-center justify-center gap-1.5">
                <CheckCircle2 size={13} className="text-green-500" />
                {emptyText}
            </p>
        ) : (
            jobs.map((job) => (
                <JobRow key={job.jobId} job={job} onClick={() => onJob(job)} />
            ))
        )}
    </div>
);

const JobsAttentionPanel = ({ attention, loading }) => {
    const navigate = useNavigate();
    const noApps = attention?.noApplications ?? [];
    const stale = attention?.stale ?? [];

    // Click-through to the job detail. Recruitment job detail lives at
    // /admin/jobs/:id (mirrors the router's job-detail route).
    const goToJob = (job) => navigate(`/admin/job-detail/${job.jobId}`);

    return (
        <WidgetCard
            icon={AlertTriangle}
            title="Jobs Needing Attention"
            insight={attention?.insight}
            loading={loading}
            right={
                attention?.openJobsTotal != null ? (
                    <span className="text-[11px] text-text-secondary">
                        {attention.openJobsTotal} open
                    </span>
                ) : null
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Column
                    title="No applications"
                    icon={Inbox}
                    tint="#f59e0b"
                    jobs={noApps}
                    loading={loading}
                    onJob={goToJob}
                    emptyText="Every open job has applicants."
                />
                <Column
                    title="Stalled 14+ days"
                    icon={Clock}
                    tint="#ef4444"
                    jobs={stale}
                    loading={loading}
                    onJob={goToJob}
                    emptyText="No stalled jobs."
                />
            </div>
        </WidgetCard>
    );
};

export default JobsAttentionPanel;
